import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { ArrowLeft, Pause, RotateCcw, Home, ListVideo, Play } from "lucide-react";

const HEARTBEAT_MS = 10000;
const CHROME_IDLE_MS = 2500;
const COUNTDOWN_SECONDS = 10;

/**
 * BrandedPlayer — Phase 7 branded chrome + Up Next overlay.
 *
 *  - Wraps a YouTube/Vimeo embed with tenant-branded top bar (logo + title + back arrow).
 *  - Auto-hides chrome on 2.5s mouse idle (Netflix-style).
 *  - Renders a "Paused — {tenant}" pill bottom-right when state=PAUSED/BUFFERING — masks the
 *    "More videos" preview + YT watermark zone.
 *  - On video ENDED: hides iframe (opacity:0; pointer-events:none), fetches /me/up-next,
 *    shows a full-screen branded overlay with an auto-countdown to the next title.
 *  - View-event tracking (view_start / heartbeat every 10s / view_complete) preserved from
 *    Phase 4. view_complete is fired BEFORE the overlay shows.
 *
 * Props:
 *   tenant            — { name, logo_url, theme_color, accent_color }
 *   tenantSlug        — string (used by view-event payloads + overlay nav)
 *   content           — { id, slug, title, kind }
 *   episodeId         — string | null
 *   episodeLabel      — string | null (e.g. "S1E3" — shown in chrome title)
 *   videoSource       — { type: "youtube"|"vimeo", video_id }
 *   initialPositionSeconds — number
 */
export default function BrandedPlayer({
  tenant,
  tenantSlug,
  content,
  episodeId = null,
  episodeLabel = null,
  videoSource,
  initialPositionSeconds = 0,
}) {
  const navigate = useNavigate();
  const { sp } = useStore();
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytReadyRef = useRef(false);
  const stateRef = useRef({
    position: initialPositionSeconds || 0,
    duration: 0,
    completed: false,
    lastSavedAt: 0,
    lastEventAt: 0,
    sessionId:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `bp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    startedFired: false,
    completeFired: false,
    playerState: -1,
    playingOnce: false,
  });

  const [error, setError] = useState("");
  const [chromeVisible, setChromeVisible] = useState(true);
  const [showPausedMask, setShowPausedMask] = useState(false);
  const [endOverlay, setEndOverlay] = useState(null); // { upNext, fadingIframe }
  // Phase 8 — branded poster covers the YT cued/unstarted state. We only flip it
  // off after the user explicitly clicks our play button; the iframe is mounted
  // BEHIND the poster (z-10 vs z-30) so YouTube's red play button + "Watch on
  // YouTube" link never become visible. Re-mounted on overlay dismissal too.
  const [showPoster, setShowPoster] = useState(true);
  const [posterFading, setPosterFading] = useState(false);

  const themeColor = tenant?.theme_color || "#7C3AED";
  const accentColor = tenant?.accent_color || "#F59E0B";

  // ----- view event tracking -----
  const fireEvent = useCallback(async (kind) => {
    if (!tenantSlug || !content?.id) return;
    try {
      await api.post("/events/view", {
        tenant_slug: tenantSlug,
        content_item_id: content.id,
        episode_id: episodeId || undefined,
        source: content.kind === "series" ? "series" : (content.kind === "reel" ? "reel" : "movie"),
        kind,
        position_seconds: Math.floor(stateRef.current.position || 0),
        duration_seconds: Math.floor(stateRef.current.duration || 0),
        session_id: stateRef.current.sessionId,
      });
    } catch (e) { /* analytics is best-effort */ }
  }, [tenantSlug, content?.id, content?.kind, episodeId]);

  const saveProgress = useCallback(async (force = false) => {
    const s = stateRef.current;
    if (!content?.id || s.duration <= 0 || s.position < 0) return;
    // Phase 8 fix — never POST position=0 over a seeded resume point. The YT
    // poll runs while the iframe is in CUED state and reports getCurrentTime()=0
    // until the user actually presses play; without this guard, that 0 clobbers
    // a user's resume seed within 5s of mounting the watch page.
    if (s.position < 1 && !s.playingOnce) return;
    const now = Date.now();
    if (!force && now - s.lastSavedAt < 8000) return;
    s.lastSavedAt = now;
    try {
      await api.post("/me/progress", {
        content_item_id: content.id,
        episode_id: episodeId || null,
        position_seconds: Math.floor(s.position),
        duration_seconds: Math.floor(s.duration),
        completed: s.duration > 0 && s.position / s.duration >= 0.95,
      });
    } catch (e) { /* silent */ }
    if (force) return;
    if (now - s.lastEventAt > HEARTBEAT_MS - 1000) {
      s.lastEventAt = now;
      fireEvent("heartbeat");
    }
    if (!s.completeFired && s.position / s.duration >= 0.95) {
      s.completeFired = true;
      fireEvent("view_complete");
    }
  }, [content?.id, episodeId, fireEvent]);

  // ----- ENDED handler — fetch up-next, hide iframe, show overlay -----
  const onEnded = useCallback(async () => {
    // Always fire view_complete first (idempotent — guarded by completeFired)
    if (!stateRef.current.completeFired) {
      stateRef.current.completeFired = true;
      try {
        await fireEvent("view_complete");
      } catch (e) { /* ignore */ }
    }
    let upNext = null;
    try {
      const res = await api.get("/me/up-next", {
        params: {
          content_item_id: content.id,
          episode_id: episodeId || undefined,
        },
      });
      if (res.data && res.data.kind && res.data.kind !== "none") upNext = res.data;
    } catch (e) { /* fall through with upNext=null */ }
    setEndOverlay({ upNext, fadingIframe: true });
  }, [content?.id, episodeId, fireEvent]);

  const restoreFromEnd = useCallback(() => {
    setEndOverlay(null);
    try {
      ytPlayerRef.current?.seekTo?.(0, true);
      ytPlayerRef.current?.playVideo?.();
    } catch (e) { /* ignore */ }
    if (videoSource?.type === "vimeo") {
      const iframe = containerRef.current?.querySelector("iframe");
      iframe?.contentWindow?.postMessage(
        JSON.stringify({ method: "setCurrentTime", value: 0 }), "*");
      iframe?.contentWindow?.postMessage(
        JSON.stringify({ method: "play" }), "*");
    }
    stateRef.current.completeFired = false;
  }, [videoSource?.type]);

  // Phase 8 — explicit play from the branded poster. Calls into the YT IFrame
  // API (or postMessage for Vimeo) and fades the poster out. If we have a
  // resume position, seek there first.
  const playFromPoster = useCallback(() => {
    const seekTo = Math.floor(initialPositionSeconds || 0);
    try {
      if (videoSource?.type === "youtube" && ytPlayerRef.current) {
        if (seekTo > 0 && typeof ytPlayerRef.current.seekTo === "function") {
          ytPlayerRef.current.seekTo(seekTo, true);
        }
        ytPlayerRef.current.playVideo?.();
      } else if (videoSource?.type === "vimeo") {
        const iframe = containerRef.current?.querySelector("iframe");
        if (iframe?.contentWindow) {
          if (seekTo > 0) {
            iframe.contentWindow.postMessage(JSON.stringify({ method: "setCurrentTime", value: seekTo }), "*");
          }
          iframe.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
        }
      }
    } catch (e) { /* ignore */ }
    setPosterFading(true);
    setTimeout(() => setShowPoster(false), 320);
  }, [videoSource?.type, initialPositionSeconds]);

  // Phase 8 — when the up-next overlay is dismissed without a navigation
  // (Replay / All episodes / Back to home), the YT player is back in CUED
  // state. Re-show the poster so the YT end-screen grid stays masked.
  // (Replay dismisses + restarts playback — we don't re-show poster there.)
  // Currently triggered only when the countdown completes WITHOUT navigation
  // (won't happen because countdown auto-navigates) OR when Back to home
  // is clicked (which navigates away anyway). So nothing to do here for now.

  // ----- view_start once on mount -----
  useEffect(() => {
    if (!stateRef.current.startedFired && tenantSlug && content?.id) {
      stateRef.current.startedFired = true;
      fireEvent("view_start");
    }
    return () => { saveProgress(true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.id, episodeId]);

  // ----- YouTube IFrame API -----
  useEffect(() => {
    if (!videoSource || videoSource.type !== "youtube") return;
    let interval = null;
    let cancelled = false;

    const ensureApi = () =>
      new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve();
        if (document.getElementById("yt-iframe-api")) {
          const i = setInterval(() => {
            if (window.YT && window.YT.Player) {
              clearInterval(i);
              resolve();
            }
          }, 100);
          return;
        }
        const tag = document.createElement("script");
        tag.id = "yt-iframe-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof prev === "function") prev();
          resolve();
        };
      });

    const start = async () => {
      await ensureApi();
      if (cancelled || !containerRef.current) return;
      try {
        ytPlayerRef.current = new window.YT.Player(containerRef.current, {
          videoId: videoSource.video_id,
          host: "https://www.youtube-nocookie.com",
          playerVars: {
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            disablekb: 1,
            fs: 1,
            controls: 1,
            playsinline: 1,
            cc_load_policy: 0,
            enablejsapi: 1,
            origin: window.location.origin,
            start: Math.floor(initialPositionSeconds || 0),
          },
          events: {
            onReady: () => {
              ytReadyRef.current = true;
              interval = setInterval(() => {
                try {
                  const p = ytPlayerRef.current;
                  const pos = p?.getCurrentTime?.() || 0;
                  const dur = p?.getDuration?.() || 0;
                  stateRef.current.position = pos;
                  stateRef.current.duration = dur;
                  saveProgress(false);
                } catch (e) {}
              }, 5000);
            },
            onStateChange: (e) => {
              // YT.PlayerState: -1 unstarted, 0 ENDED, 1 PLAYING, 2 PAUSED, 3 BUFFERING, 5 CUED
              stateRef.current.playerState = e.data;
              if (e.data === 1) {
                setShowPausedMask(false);
                stateRef.current.playingOnce = true;
                // Once playback actually begins, retire the poster (covers any
                // race where the user clicked our play button BEFORE the YT API
                // was ready and we simply waited).
                if (showPoster) {
                  setPosterFading(true);
                  setTimeout(() => setShowPoster(false), 320);
                }
              } else if (e.data === -1 || e.data === 2 || e.data === 3 || e.data === 5) {
                // UNSTARTED / PAUSED / BUFFERING / CUED — all states where YT
                // branding (red play button, watermark, "More videos") would
                // leak through. The branded poster covers the entire iframe
                // for the initial cued state; the bottom-right pill covers
                // the paused/buffering states once playback has started.
                setShowPausedMask(true);
              }
              if (e.data === 0) {
                onEnded();
              }
              if (e.data === 0 || e.data === 2) saveProgress(true);
            },
            onError: () => setError("YouTube reported an error loading this video."),
          },
        });
      } catch (e) {
        setError("Failed to initialize YouTube player.");
      }
    };
    start();
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      try { ytPlayerRef.current?.destroy?.(); } catch (e) {}
      ytPlayerRef.current = null;
      ytReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSource?.type, videoSource?.video_id, content?.id, episodeId]);

  // ----- Vimeo postMessage -----
  useEffect(() => {
    if (!videoSource || videoSource.type !== "vimeo") return;
    const onMsg = (event) => {
      if (!event.origin.includes("vimeo.com")) return;
      let data = event.data;
      try {
        if (typeof data === "string") data = JSON.parse(data);
      } catch (e) { return; }
      if (data?.event === "ready") {
        const iframe = containerRef.current?.querySelector("iframe");
        if (iframe?.contentWindow) {
          ["timeupdate", "pause", "play", "ended", "bufferstart", "bufferend"].forEach((evt) => {
            iframe.contentWindow.postMessage(JSON.stringify({ method: "addEventListener", value: evt }), "*");
          });
        }
      }
      if (data?.event === "timeupdate" && data.data) {
        stateRef.current.position = data.data.seconds || 0;
        stateRef.current.duration = data.data.duration || 0;
        saveProgress(false);
      }
      if (data?.event === "play") { setShowPausedMask(false); stateRef.current.playingOnce = true; }
      if (data?.event === "pause" || data?.event === "bufferstart") setShowPausedMask(true);
      if (data?.event === "ended") { saveProgress(true); onEnded(); }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSource?.type, videoSource?.video_id]);

  // ----- chrome auto-hide on mouse-idle -----
  // NOTE: must attach to the OUTER container (rootRef), not the iframe-wrap.
  // The YT iframe absorbs all pointer events and does NOT bubble mousemove to
  // its parent document, so a listener on the wrap never fires while the
  // cursor is over the player. Listening on the outer container catches mouse
  // moves over the chrome overlays themselves (top bar, paused mask) and the
  // iframe edges — enough to re-reveal the chrome Netflix-style.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let timer = null;
    const reveal = () => {
      setChromeVisible(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setChromeVisible(false), CHROME_IDLE_MS);
    };
    reveal();
    root.addEventListener("mousemove", reveal);
    root.addEventListener("touchstart", reveal);
    root.addEventListener("mouseenter", reveal);
    return () => {
      root.removeEventListener("mousemove", reveal);
      root.removeEventListener("touchstart", reveal);
      root.removeEventListener("mouseenter", reveal);
      if (timer) clearTimeout(timer);
    };
  }, [content?.id]);

  if (!videoSource) {
    return (
      <div className="aspect-video w-full bg-black grid place-items-center text-white/40 text-xs uppercase tracking-widest" data-testid="branded-player-empty">
        No video
      </div>
    );
  }

  if (error) {
    return (
      <div className="aspect-video w-full bg-black grid place-items-center text-red-400 text-sm" data-testid="branded-player-error">
        {error}
      </div>
    );
  }

  const fadeIframe = !!endOverlay;

  return (
    <div
      ref={rootRef}
      className="relative w-full aspect-video bg-black overflow-hidden group"
      style={{ boxShadow: `0 0 60px -10px ${themeColor}88` }}
      data-testid="branded-player"
    >
      {/* Iframe (YT API replaces the div; Vimeo uses a static iframe with tightened params) */}
      <div
        className="absolute inset-0 transition-opacity duration-200"
        style={{ opacity: fadeIframe ? 0 : 1, pointerEvents: fadeIframe ? "none" : "auto" }}
        data-testid="branded-player-iframe-wrap"
      >
        {videoSource.type === "youtube" ? (
          <div ref={containerRef} className="w-full h-full" />
        ) : (
          <div ref={containerRef} className="w-full h-full">
            <iframe
              title="Video player"
              src={`https://player.vimeo.com/video/${videoSource.video_id}?autoplay=0&dnt=1&title=0&byline=0&portrait=0&playsinline=1&pip=1&api=1#t=${Math.floor(initialPositionSeconds || 0)}s`}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"
              data-testid="branded-player-vimeo-iframe"
            />
          </div>
        )}
      </div>

      {/* Phase 8 — Branded poster covers YT cued/unstarted state until the user
          explicitly clicks our play button. z-30 sits ABOVE the iframe (z-10)
          so YouTube's red play button + "Watch on YouTube" link never leak. */}
      {showPoster && !endOverlay && (
        <BrandedPoster
          tenant={tenant}
          content={content}
          episodeLabel={episodeLabel}
          themeColor={themeColor}
          accentColor={accentColor}
          fading={posterFading}
          resumePositionSeconds={initialPositionSeconds || 0}
          onPlay={playFromPoster}
          onBack={() => navigate(sp(`/title/${content.slug}`))}
        />
      )}

      {/* Branded top bar — covers YT title bar zone */}
      <div
        className={`absolute top-0 inset-x-0 z-10 transition-opacity duration-300 ${chromeVisible || showPausedMask ? "opacity-100" : "opacity-0"} ${endOverlay ? "hidden" : ""}`}
        style={{
          backgroundImage: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 60%, transparent 100%)",
          height: 96,
          pointerEvents: "none",
        }}
        data-testid="branded-player-top-bar"
      >
        <div className="flex items-center gap-3 px-4 md:px-6 pt-3">
          <button
            type="button"
            onClick={() => navigate(sp(`/title/${content.slug}`))}
            className="grid place-items-center w-9 h-9 border border-white/30 bg-black/40 hover:bg-black/70 transition-colors"
            style={{ pointerEvents: "auto" }}
            aria-label="Back to title page"
            data-testid="branded-player-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.name} className="h-8 w-8 object-contain" />
          ) : (
            <div className="grid place-items-center h-8 w-8 text-[10px] uppercase tracking-[0.25em] font-bold" style={{ backgroundColor: themeColor, color: "#0a0a0a" }}>
              {tenant?.name?.[0] || "S"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{tenant?.name}</div>
            <div className="font-display font-semibold text-sm md:text-base tracking-tight truncate" data-testid="branded-player-title">
              {episodeLabel ? `${episodeLabel} · ` : ""}{content?.title}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom-right paused mask — covers YT "More videos" + watermark zone */}
      {showPausedMask && !endOverlay && (
        <div
          className="absolute bottom-3 right-3 md:bottom-6 md:right-6 z-10 px-4 py-3 backdrop-blur-md bg-black/75 border border-white/15 flex items-center gap-3 max-w-[280px] pointer-events-none"
          data-testid="branded-player-paused-mask"
        >
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt="" className="h-6 w-6 object-contain" />
          ) : (
            <div className="grid place-items-center h-6 w-6 text-[8px] uppercase tracking-[0.2em] font-bold" style={{ backgroundColor: themeColor, color: "#0a0a0a" }}>
              {tenant?.name?.[0] || "S"}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 inline-flex items-center gap-1">
              <Pause className="w-3 h-3" /> Paused
            </div>
            <div className="text-xs font-medium truncate">{tenant?.name}</div>
          </div>
        </div>
      )}

      {/* End-of-video Up Next overlay */}
      {endOverlay && (
        <UpNextOverlay
          upNext={endOverlay.upNext}
          tenant={tenant}
          tenantSlug={tenantSlug}
          content={content}
          accentColor={accentColor}
          themeColor={themeColor}
          onReplay={restoreFromEnd}
        />
      )}
    </div>
  );
}

function UpNextOverlay({ upNext, tenant, tenantSlug, content, accentColor, themeColor, onReplay }) {
  const navigate = useNavigate();
  const { sp } = useStore();
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [paused, setPaused] = useState(false);

  const navigateToNext = useCallback(() => {
    if (!upNext) return;
    if (upNext.kind === "episode" && upNext.parent_series_slug) {
      // The watch route expects /watch/{seriesSlug}/s{n}/e{n+1} — we don't have season/episode
      // numbers in the payload, so navigate to the title page; the title page season/episode
      // selector takes the user to the right episode.
      navigate(sp(`/title/${upNext.parent_series_slug}`));
      return;
    }
    const t = upNext.resume_position_seconds || 0;
    const q = t > 5 ? `?t=${Math.floor(t)}` : "";
    navigate(sp(`/watch/${upNext.slug}${q}`));
  }, [upNext, tenantSlug, navigate]);

  const navigateToPrior = useCallback(() => {
    const p = upNext?.prior_in_progress;
    if (!p) return;
    const t = p.position_seconds || 0;
    const q = t > 5 ? `?t=${Math.floor(t)}` : "";
    navigate(sp(`/watch/${p.slug}${q}`));
  }, [upNext, tenantSlug, navigate]);

  const resumeMM = upNext?.resume_position_seconds
    ? `${Math.floor(upNext.resume_position_seconds / 60)}:${String(Math.floor(upNext.resume_position_seconds % 60)).padStart(2, "0")}`
    : null;

  useEffect(() => {
    if (!upNext || paused) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          navigateToNext();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [upNext, paused, navigateToNext]);

  return (
    <div
      className="absolute inset-0 z-20 bg-[#0a0a0a]/95 backdrop-blur-sm grid place-items-center px-4 md:px-8"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="branded-player-up-next"
    >
      {/* Backdrop blur of next item's hero banner */}
      {upNext?.hero_banner_url && (
        <div
          className="absolute inset-0 opacity-25 mix-blend-luminosity pointer-events-none"
          style={{
            backgroundImage: `url(${upNext.hero_banner_url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(20px) saturate(1.4)",
          }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-br pointer-events-none" style={{
        backgroundImage: `linear-gradient(135deg, ${themeColor}22, transparent 40%, ${accentColor}11 100%)`,
      }} />
      <div className="relative w-full max-w-3xl text-center">
        <div className="flex items-center justify-center gap-2 mb-5" data-testid="up-next-tenant-badge">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.name} className="h-6 w-6 object-contain" />
          ) : (
            <div className="grid place-items-center h-6 w-6 text-[8px] uppercase tracking-[0.2em] font-bold" style={{ backgroundColor: themeColor, color: "#0a0a0a" }}>
              {tenant?.name?.[0] || "S"}
            </div>
          )}
          <span className="text-[11px] uppercase tracking-[0.4em] text-white/55">{tenant?.name}</span>
        </div>
        <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95]" data-testid="up-next-headline">
          You finished watching
        </h2>
        <p className="mt-2 text-white/65 text-sm md:text-base font-light truncate">{content?.title}</p>

        {upNext ? (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 md:gap-7 items-center bg-black/50 border border-white/15 p-4 md:p-5 text-left" data-testid="up-next-card">
            <div className="aspect-video bg-black border border-white/10 overflow-hidden md:aspect-[16/10] relative">
              {upNext.thumbnail_url && (
                <img src={upNext.thumbnail_url} alt="" className="w-full h-full object-cover" />
              )}
              {upNext.resume_percent > 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15" data-testid="up-next-card-progress">
                  <div className="h-full" style={{ width: `${Math.min(100, upNext.resume_percent)}%`, backgroundColor: accentColor }} />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/45 mb-1">
                {upNext.kind === "episode" ? `Up next · ${upNext.episode_label || "Next episode"}` : "Up next"}
                {resumeMM && <span className="ml-2 text-amber-300/85">· Resume {resumeMM}</span>}
              </div>
              <div className="font-display font-bold text-xl md:text-2xl tracking-tight" data-testid="up-next-title">
                {upNext.title}
              </div>
              {upNext.description && (
                <p className="mt-2 text-white/60 text-sm font-light line-clamp-2">{upNext.description}</p>
              )}
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={navigateToNext}
                  className="inline-flex items-center gap-2 px-5 h-11 text-xs uppercase tracking-[0.25em] font-bold transition-transform hover:-translate-y-0.5"
                  style={{ backgroundColor: accentColor, color: "#0a0a0a" }}
                  data-testid="up-next-watch-now"
                >
                  <Play className="w-4 h-4 fill-black" />
                  {resumeMM ? `Resume from ${resumeMM}` : "Watch now"}
                </button>
                <CountdownRing seconds={secondsLeft} total={COUNTDOWN_SECONDS} accentColor={accentColor} paused={paused} />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 text-white/55 text-sm" data-testid="up-next-empty">
            That's the latest from {tenant?.name}. More coming soon.
          </div>
        )}

        {/* Phase 8 — secondary "or continue" card if user has a different in-progress title */}
        {upNext?.prior_in_progress && (
          <button
            type="button"
            onClick={navigateToPrior}
            className="mt-4 w-full text-left flex items-center gap-3 p-3 border border-white/10 hover:border-white/30 bg-black/40 backdrop-blur-sm transition-colors"
            data-testid="up-next-prior"
          >
            <div className="relative w-20 h-12 bg-black border border-white/10 overflow-hidden flex-shrink-0">
              {upNext.prior_in_progress.thumbnail_url && (
                <img src={upNext.prior_in_progress.thumbnail_url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15">
                <div className="h-full" style={{ width: `${upNext.prior_in_progress.percent}%`, backgroundColor: accentColor }} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">Or continue · {upNext.prior_in_progress.percent}% watched</div>
              <div className="text-sm font-medium truncate" data-testid="up-next-prior-title">
                {upNext.prior_in_progress.episode_label ? `${upNext.prior_in_progress.episode_label} · ` : ""}{upNext.prior_in_progress.title}
              </div>
            </div>
            <Play className="w-4 h-4 text-white/55" />
          </button>
        )}

        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap" data-testid="up-next-secondary-actions">
          <button
            type="button"
            onClick={onReplay}
            className="inline-flex items-center gap-2 px-4 h-10 text-[11px] uppercase tracking-[0.25em] border border-white/30 hover:border-white text-white/85 hover:text-white"
            data-testid="up-next-replay"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Replay
          </button>
          {content?.kind === "series" && (
            <button
              type="button"
              onClick={() => navigate(sp(`/title/${content.slug}`))}
              className="inline-flex items-center gap-2 px-4 h-10 text-[11px] uppercase tracking-[0.25em] border border-white/30 hover:border-white text-white/85 hover:text-white"
              data-testid="up-next-all-episodes"
            >
              <ListVideo className="w-3.5 h-3.5" /> All episodes
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(sp(`/home`))}
            className="inline-flex items-center gap-2 px-4 h-10 text-[11px] uppercase tracking-[0.25em] border border-white/30 hover:border-white text-white/85 hover:text-white"
            data-testid="up-next-home"
          >
            <Home className="w-3.5 h-3.5" /> Back to home
          </button>
        </div>
      </div>
    </div>
  );
}

function CountdownRing({ seconds, total, accentColor, paused }) {
  const r = 14;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, seconds / total));
  return (
    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/55" data-testid="up-next-countdown">
      <svg width={36} height={36} viewBox="0 0 36 36" className="-rotate-90">
        <circle cx="18" cy="18" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2.5" />
        <circle
          cx="18" cy="18" r={r} fill="none" stroke={accentColor} strokeWidth="2.5"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
      </svg>
      <span>{paused ? "Paused" : `Next in ${seconds}s`}</span>
    </div>
  );
}



// ----------------------------------------------------------------------------
// Phase 8 — Branded poster overlay. Covers the iframe completely while it sits
// in YT's CUED state so the red play button + "Watch on YouTube" never appear.
// ----------------------------------------------------------------------------
function BrandedPoster({
  tenant,
  content,
  episodeLabel,
  themeColor,
  accentColor,
  fading,
  resumePositionSeconds,
  onPlay,
  onBack,
}) {
  const bg = content?.hero_banner_url || content?.hero_image_url || content?.thumbnail_url || "";
  const hasResume = Boolean(resumePositionSeconds) && resumePositionSeconds > 5;
  const mm = Math.floor((resumePositionSeconds || 0) / 60);
  const ss = String(Math.floor((resumePositionSeconds || 0) % 60)).padStart(2, "0");
  return (
    <div
      className={`absolute inset-0 z-30 transition-opacity duration-300 ${fading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      data-testid="branded-player-poster"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={bg ? { backgroundImage: `url(${bg})` } : { background: `linear-gradient(135deg, ${themeColor}55, #0a0a0a 70%)` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/35 to-black/80" />
      <div className="absolute inset-0 grid place-items-center px-6 md:px-10">
        <div className="relative w-full max-w-2xl text-center">
          <div className="flex items-center justify-center gap-2 mb-5">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={tenant.name} className="h-7 w-7 object-contain" />
            ) : (
              <div className="grid place-items-center h-7 w-7 text-[9px] uppercase tracking-[0.25em] font-bold" style={{ backgroundColor: themeColor, color: "#0a0a0a" }}>
                {tenant?.name?.[0] || "S"}
              </div>
            )}
            <span className="text-[11px] uppercase tracking-[0.4em] text-white/65" data-testid="poster-tenant-name">{tenant?.name}</span>
          </div>
          <h2
            className="font-display font-black text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
            data-testid="poster-title"
          >
            {episodeLabel ? `${episodeLabel} · ` : ""}{content?.title}
          </h2>
          {content?.description && (
            <p className="mt-4 text-white/70 text-sm md:text-base font-light max-w-xl mx-auto line-clamp-3">{content.description}</p>
          )}
          <div className="mt-8 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={onPlay}
              className="inline-flex items-center gap-2 px-7 md:px-9 h-14 md:h-16 text-sm md:text-base font-bold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5 active:translate-y-0"
              style={{ backgroundColor: accentColor, color: "#0a0a0a" }}
              data-testid="poster-play-button"
              autoFocus
            >
              <Play className="w-5 h-5 fill-black" />
              {hasResume ? `Resume at ${mm}:${ss}` : "Play"}
            </button>
            {hasResume && (
              <button
                type="button"
                onClick={onPlay}
                className="text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-white"
                data-testid="poster-restart-link"
                title="Resume picks up where you left off"
              >
                Resume from where you left off
              </button>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="absolute top-4 left-4 md:top-6 md:left-6 z-10 grid place-items-center w-10 h-10 border border-white/30 bg-black/40 hover:bg-black/70 transition-colors"
        aria-label="Back"
        data-testid="poster-back"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
    </div>
  );
}
